import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
    Alert, Box, Button, Checkbox, Chip, Divider, FormControlLabel, Grid,
    IconButton, Paper, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from "@mui/material";
import Modal from "../Modal/Modal";
import SimpleBackdrop from "../Loading/SimpleBackdrop";
import { AlertInfo } from "../Alerts/Alert";
import { api } from "../../services/api";
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export type SignerInit = {
    name: string
    email?: string
    phone?: string
    cpf?: string
    role?: 'cliente' | 'advogado' | string
    label?: string
}

type SignerState = SignerInit & {
    enviar_email: boolean
    enviar_whatsapp: boolean
}

type ResultSigner = {
    name: string
    sign_url: string
    status?: string
}

type QuemAssina = 'ambos' | 'cliente' | 'advogado';

type Props = {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    tipo: 'avulso' | 'processo'
    documentFile: string
    nomeDocumento: string
    dadosDocumento: any
    id_processo?: number | null
    id_cliente?: number | null
    signersIniciais: SignerInit[]
    config: { zapsign_envio_email: number; zapsign_envio_whatsapp: number }
}

export default function ModalEnviarAssinatura({
    open, setOpen, tipo, documentFile, nomeDocumento, dadosDocumento,
    id_processo = null, id_cliente = null, signersIniciais, config
}: Props) {
    const [load, setLoad] = useState(false);
    const [signers, setSigners] = useState<SignerState[]>([]);
    const [quemAssina, setQuemAssina] = useState<QuemAssina>('ambos');
    const [resultado, setResultado] = useState<ResultSigner[] | null>(null);

    useEffect(() => {
        if (open) {
            setResultado(null);
            setQuemAssina('ambos');
            setSigners(signersIniciais.map(s => ({
                ...s,
                enviar_email: !!config.zapsign_envio_email && !!s.email,
                enviar_whatsapp: !!config.zapsign_envio_whatsapp && !!s.phone,
            })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // true se o signatário deve assinar conforme a opção escolhida.
    // Qualquer papel diferente de "advogado" é tratado como cliente.
    function signerSelecionado(s: SignerState) {
        if (quemAssina === 'ambos') return true;
        if (quemAssina === 'advogado') return s.role === 'advogado';
        return s.role !== 'advogado';
    }

    // papéis disponíveis para habilitar/desabilitar as opções do seletor
    const temCliente = signers.some(s => s.role !== 'advogado');
    const temAdvogado = signers.some(s => s.role === 'advogado');

    function updateSigner(idx: number, campo: keyof SignerState, valor: any) {
        setSigners(prev => prev.map((s, i) => i === idx ? { ...s, [campo]: valor } : s));
    }

    async function handleEnviar() {
        const enviados = signers.filter(signerSelecionado);
        if (enviados.length === 0) {
            return AlertInfo({ icon: 'info', title: 'Selecione ao menos um signatário' });
        }
        if (enviados.some(s => !s.name || !s.name.trim())) {
            return AlertInfo({ icon: 'info', title: 'Todos os signatários precisam de nome' });
        }
        setLoad(true);
        try {
            const response = await api.post('/enviardocumentozapsign', {
                tipo,
                documentFile,
                id_processo,
                id_cliente,
                nome_documento: nomeDocumento,
                dadosDocumento,
                signers: enviados.map(s => ({
                    name: s.name,
                    email: s.email || '',
                    phone: s.phone || '',
                    cpf: s.cpf || '',
                    role: s.role === 'advogado' ? 'advogado' : 'cliente',
                    enviar_email: s.enviar_email,
                    enviar_whatsapp: s.enviar_whatsapp,
                })),
            });
            if (response.data.success) {
                setResultado(response.data.signers || []);
            } else {
                AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao enviar para assinatura' });
            }
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setLoad(false);
    }

    function copiarLink(url: string) {
        navigator.clipboard.writeText(url)
            .then(() => AlertInfo({ icon: 'success', title: 'Link copiado!' }))
            .catch(() => AlertInfo({ icon: 'error', title: 'Não foi possível copiar' }));
    }

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title="Enviar para assinatura (ZapSign)"
            maxWidth="sm"
            zIndex={3333}
        >
            <SimpleBackdrop open={load} />

            {/* RESULTADO — links de assinatura gerados */}
            {resultado ? (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Alert severity="success" icon={<CheckCircleIcon />}>
                            Documento enviado para assinatura! Copie e compartilhe os links abaixo.
                        </Alert>
                    </Grid>
                    {resultado.map((r, i) => (
                        <Grid item xs={12} key={i}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                    {r.name}
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={r.sign_url}
                                        InputProps={{ readOnly: true, sx: { fontSize: 12 } }}
                                    />
                                    <Tooltip title="Copiar link">
                                        <IconButton color="primary" onClick={() => copiarLink(r.sign_url)}>
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Abrir link">
                                        <IconButton onClick={() => window.open(r.sign_url, '_blank')}>
                                            <OpenInNewIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Button variant="contained" fullWidth onClick={() => setOpen(false)}>
                            Concluir
                        </Button>
                    </Grid>
                </Grid>
            ) : (
                /* FORMULÁRIO — signatários */
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Confira os dados dos signatários. O <strong>link de assinatura</strong> será exibido
                            aqui para você copiar. O envio automático por e-mail/WhatsApp ocorre apenas se marcado.
                        </Alert>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Quem vai assinar o documento?
                        </Typography>
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            fullWidth
                            size="small"
                            value={quemAssina}
                            onChange={(_, v) => { if (v) setQuemAssina(v); }}
                        >
                            <ToggleButton value="ambos" disabled={!temCliente || !temAdvogado}>
                                Cliente e advogado
                            </ToggleButton>
                            <ToggleButton value="cliente" disabled={!temCliente}>
                                Somente cliente
                            </ToggleButton>
                            <ToggleButton value="advogado" disabled={!temAdvogado}>
                                Somente advogado
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {signers.map((s, idx) => {
                        if (!signerSelecionado(s)) return null;
                        return (
                        <Grid item xs={12} key={idx}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Divider sx={{ mb: 1.5 }}>
                                    <Chip
                                        size="small"
                                        color="primary"
                                        icon={s.role === 'advogado' ? <GavelIcon /> : <PersonIcon />}
                                        label={s.label || (s.role === 'advogado' ? 'Advogado' : 'Cliente')}
                                    />
                                </Divider>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Dados do cadastro (somente leitura).
                                </Typography>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={7}>
                                        <TextField
                                            fullWidth size="small" label="Nome"
                                            value={s.name}
                                            onChange={e => updateSigner(idx, 'name', e.target.value)}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            fullWidth size="small" label="CPF"
                                            value={s.cpf || ''}
                                            onChange={e => updateSigner(idx, 'cpf', e.target.value)}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={7}>
                                        <TextField
                                            fullWidth size="small" label="E-mail"
                                            value={s.email || ''}
                                            onChange={e => updateSigner(idx, 'email', e.target.value)}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            fullWidth size="small" label="Telefone / WhatsApp"
                                            value={s.phone || ''}
                                            onChange={e => updateSigner(idx, 'phone', e.target.value)}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box display="flex" gap={2} flexWrap="wrap">
                                            <Tooltip title={config.zapsign_envio_email ? '' : 'Envio por e-mail desativado nas Configurações'}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={s.enviar_email}
                                                            disabled={!config.zapsign_envio_email}
                                                            onChange={e => updateSigner(idx, 'enviar_email', e.target.checked)}
                                                        />
                                                    }
                                                    label={<Typography variant="body2">Enviar por e-mail</Typography>}
                                                />
                                            </Tooltip>
                                            <Tooltip title={config.zapsign_envio_whatsapp ? '' : 'Envio por WhatsApp desativado nas Configurações'}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={s.enviar_whatsapp}
                                                            disabled={!config.zapsign_envio_whatsapp}
                                                            onChange={e => updateSigner(idx, 'enviar_whatsapp', e.target.checked)}
                                                        />
                                                    }
                                                    label={<Typography variant="body2">Enviar por WhatsApp</Typography>}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                        );
                    })}

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="success"
                            fullWidth
                            onClick={handleEnviar}
                        >
                            ENVIAR PARA ASSINATURA
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Modal>
    );
}
